import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { getCompaniesAll, createCompany, updateCompany, deleteCompany, getCountries } from '../utils/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const Companies = () => {
  usePageTitle('Companies');

  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    country: '',
    address: '',
    company_id: '',
    vat_id: '',
    is_vat_payer: false,
  });

  useEffect(() => {
    loadCompanies();
    loadCountries();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await getCompaniesAll();
      setCompanies(response.data);
    } catch (error) {
      toast.error('Error loading companies');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await getCountries();
      setCountries(response.data);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const openModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name || '',
        code: company.code || '',
        country: company.country || '',
        address: company.address || '',
        company_id: company.company_id || '',
        vat_id: company.vat_id || '',
        is_vat_payer: Boolean(company.is_vat_payer),
      });
    } else {
      setEditingCompany(null);
      setFormData({
        name: '',
        code: '',
        country: '',
        address: '',
        company_id: '',
        vat_id: '',
        is_vat_payer: false,
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCompany(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.code) {
      toast.error('Company name and code are required');
      return;
    }

    setSaving(true);
    try {
      if (editingCompany) {
        await updateCompany(editingCompany.id, formData);
        toast.success('Company updated successfully');
      } else {
        await createCompany(formData);
        toast.success('Company created successfully');
      }
      closeModal();
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (company) => {
    if (company.invoice_count > 0) {
      toast.error(`Cannot delete company with ${company.invoice_count} existing invoices`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${company.name}"?`)) {
      return;
    }

    try {
      await deleteCompany(company.id);
      toast.success('Company deleted successfully');
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting company');
    }
  };

  const getCountryName = (code) => {
    const country = countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  if (loading) {
    return <div className="loading-overlay"><div className="spinner" /><span>Loading companies...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Companies</h1>
          <p className="page-subtitle">Manage your company entities</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <PlusIcon style={{ width: 18, height: 18 }} />
          Add Company
        </button>
      </div>

      <div className="card">
        {companies.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Country</th>
                  <th>Company ID</th>
                  <th>VAT ID</th>
                  <th style={{ textAlign: 'center' }}>VAT Payer</th>
                  <th>Invoices</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td className="font-mono"><strong>{company.code}</strong></td>
                    <td>{company.name}</td>
                    <td>{company.country ? getCountryName(company.country) : '-'}</td>
                    <td className="font-mono">{company.company_id || '-'}</td>
                    <td className="font-mono">{company.vat_id || '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {company.is_vat_payer ? (
                        <CheckIcon style={{ width: 18, height: 18, color: 'var(--color-accent-success)' }} />
                      ) : (
                        <XMarkIcon style={{ width: 18, height: 18, color: 'var(--color-text-tertiary)' }} />
                      )}
                    </td>
                    <td>
                      <span className="badge badge-draft">{company.invoice_count || 0}</span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => openModal(company)}
                          title="Edit"
                        >
                          <PencilIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => handleDelete(company)}
                          title="Delete"
                          style={{ color: company.invoice_count > 0 ? 'var(--color-text-tertiary)' : 'var(--color-accent-danger)' }}
                          disabled={company.invoice_count > 0}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <BuildingOfficeIcon className="empty-state-icon" />
            <p className="empty-state-title">No companies found</p>
            <p>Add your first company to get started</p>
            <button className="btn btn-primary" onClick={() => openModal()} style={{ marginTop: '1rem' }}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Add Company
            </button>
          </div>
        )}
      </div>

      {/* Company Form Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingCompany ? 'Edit Company' : 'Add New Company'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editingCompany ? 'Update Company' : 'Create Company'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Company Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter company name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Company Code *</label>
              <input
                type="text"
                className="form-input"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., MAIN, SUB1"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Country</label>
              <select
                className="form-select"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              >
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Company ID / Registration Number</label>
              <input
                type="text"
                className="form-input"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                placeholder="e.g., DE123456789"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              className="form-textarea"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Enter full address"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">VAT ID</label>
              <input
                type="text"
                className="form-input"
                value={formData.vat_id}
                onChange={(e) => setFormData({ ...formData, vat_id: e.target.value })}
                placeholder="e.g., DE123456789"
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.5rem' }}>
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.is_vat_payer}
                  onChange={(e) => setFormData({ ...formData, is_vat_payer: e.target.checked })}
                />
                <span>VAT Payer</span>
              </label>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Companies;
